import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {type SetStateAction, useCallback} from 'react'

// Pull Requests and Issues share this stored value, so they must agree on its shape.
export type CommentsSortConfig = {
  dateOrder: 'ascending' | 'descending'
  /** Sort by code order in supported views. Code order only shows review comments. */
  codeOrderOverride: boolean
}

function isCommentsSortConfig(value: unknown): value is CommentsSortConfig {
  if (typeof value !== 'object' || value === null) return false

  const {dateOrder, codeOrderOverride} = value as Record<string, unknown>
  return (dateOrder === 'ascending' || dateOrder === 'descending') && typeof codeOrderOverride === 'boolean'
}

const defaultCommentsSortConfig = {
  dateOrder: 'ascending',
  codeOrderOverride: true,
} as const satisfies CommentsSortConfig

export function validOrDefaultCommentsSortConfig(unvalidatedConfig: unknown): CommentsSortConfig {
  return isCommentsSortConfig(unvalidatedConfig) ? unvalidatedConfig : defaultCommentsSortConfig
}

export function commentsSortStorageKey(localStorageKey?: string) {
  return `pull-request-${localStorageKey ? `${localStorageKey}-` : ''}comments-sort`
}

export function useCommentsSortConfig(localStorageKey?: string) {
  return useLocalStorage(commentsSortStorageKey(localStorageKey), defaultCommentsSortConfig, {
    validator: isCommentsSortConfig,
  })
}

export const ChronologicalCommentsSort = {
  OldestFirst: 'oldest-first',
  NewestFirst: 'newest-first',
} as const

export type ChronologicalCommentsSort = (typeof ChronologicalCommentsSort)[keyof typeof ChronologicalCommentsSort]

function chronologicalSortFor(unvalidatedConfig: unknown): ChronologicalCommentsSort {
  return validOrDefaultCommentsSortConfig(unvalidatedConfig).dateOrder === 'descending'
    ? ChronologicalCommentsSort.NewestFirst
    : ChronologicalCommentsSort.OldestFirst
}

/** Oldest / Newest comment sort. Leaves the Pull Requests code order setting alone. */
export function useChronologicalCommentsSort() {
  const [unvalidatedConfig, setUnvalidatedConfig] = useCommentsSortConfig()

  const setSort = useCallback(
    (action: SetStateAction<ChronologicalCommentsSort>) => {
      setUnvalidatedConfig(current => {
        const newSort = typeof action === 'function' ? action(chronologicalSortFor(current)) : action

        return {
          ...validOrDefaultCommentsSortConfig(current),
          dateOrder: newSort === ChronologicalCommentsSort.NewestFirst ? 'descending' : 'ascending',
        }
      })
    },
    [setUnvalidatedConfig],
  )

  return [chronologicalSortFor(unvalidatedConfig), setSort] as const
}
